import { Loader2 } from 'lucide-react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from '../../lib/cn.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'surface' | 'danger' | 'success';

export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

/**
 * Variantes de design.md §8.
 *
 * A ação primária é a superfície escura, não a laranja. Laranja preenchendo
 * botão obriga a texto quase preto para passar em contraste — o pareamento de
 * placa de sinalização, que era justamente o que o sistema antigo fazia. Como
 * sinal, o laranja rende mais no ícone ativo, no link, no anel de foco e no
 * indicador de atualização, onde é o único elemento colorido da tela.
 */
const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-foreground text-white hover:bg-foreground-hover',
  secondary: 'bg-muted text-foreground hover:bg-accent',
  ghost: 'text-muted-foreground hover:bg-muted hover:text-foreground',
  /*
   * O `secondary` em cima de um painel `bg-muted` fica da cor do painel e some.
   * Aqui o preenchimento vai para o outro lado — a superfície do cartão —, que
   * é como se destaca sem recorrer a contorno.
   */
  surface: 'bg-card text-foreground hover:bg-accent',
  danger: 'bg-danger text-white hover:bg-danger-hover',
  success: 'bg-success text-white hover:bg-success-hover',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 gap-1.5 px-2.5 text-[13px]',
  md: 'h-10 gap-2 px-3.5 text-[13px]',
  lg: 'h-11 gap-2 px-5 text-sm',
  icon: 'size-10 shrink-0',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly loading?: boolean;
  readonly icon?: ReactNode;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  icon,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      // Largura não muda ao entrar em carregamento: o spinner ocupa o lugar do ícone.
      disabled={disabled === true || loading}
      aria-busy={loading}
      className={cn(
        'inline-flex items-center justify-center rounded-sm font-medium whitespace-nowrap',
        'transition-colors duration-100 ease-out',
        'disabled:cursor-not-allowed disabled:opacity-45',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 size={15} strokeWidth={2} className="animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
}
